/**
 * `BusinessSkillSuggestions` — ADR-0226. The "Business skills" group at
 * the top of the onboarding Skills step for a non-store site.
 *
 * One row per business page the homepage scan found (pricing, demo,
 * sign-up, booking, contact, status, trust → the seven business skills,
 * ADR-0222 / ADR-0224). Each row shows the detected URL prefilled in an
 * editable field and an off Toggle.
 *
 * No-auto-apply (ADR-0040): nothing is saved or enabled when the row
 * renders or when a scan result arrives. The ONLY write is the
 * merchant's own Toggle click, which goes through the existing
 * business-skills API (`PUT /v1/sites/:site_id/business-skills/:id`)
 * with its server-side URL validation and owner/admin gate. Reviewer /
 * viewer roles see the rows disabled (the server 403s regardless).
 *
 * Untrusted data: the URLs come from the merchant's own homepage (scan
 * `key_pages`). They are https-filtered upstream
 * (`suggestBusinessSkillsFromScan`), rendered only as an input value
 * (escaped by React, never an href or HTML), and re-validated by the
 * origin on save.
 */

import {
  Activity,
  CalendarDays,
  Check,
  LifeBuoy,
  Presentation,
  RefreshCw,
  ShieldCheck,
  Tag,
  UserPlus,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useEffect, useState } from 'react';

import { useOptionalHttp } from '../../api/http-context.js';
import {
  BUSINESS_SKILL_SLOTS,
  listBusinessSkills,
  putBusinessSkill,
  type BusinessSkillId,
} from '../../api/site-business-skills.js';
import { useOptionalAuth } from '../../app/auth-context.js';
import { Pill, Toggle } from '../../components/atoms/index.js';
import type { BusinessSkillSuggestion } from '../../lib/skill-recommendations.js';
import { useC } from '../../lib/theme.js';
import { SAVE_REASON_COPY, SLOT_COPY } from '../settings/business-skill-copy.js';

const monoFont = "'JetBrains Mono', monospace";
const sansFont = "'Geist', sans-serif";

const ICONS: Record<BusinessSkillId, LucideIcon> = {
  get_pricing: Tag,
  request_demo: Presentation,
  sign_up: UserPlus,
  book_meeting: CalendarDays,
  contact_support: LifeBuoy,
  get_status: Activity,
  get_trust_center: ShieldCheck,
};

/** A row to render: a detected page, or an empty slot to fill in. */
interface SlotRow {
  readonly skill_id: BusinessSkillId;
  readonly url: string;
  readonly detected: boolean;
}

interface RowState {
  readonly draft: string;
  readonly enabled: boolean;
  readonly saving: boolean;
  readonly error: string | null;
  readonly warning: string | null;
}

export interface BusinessSkillSuggestionsProps {
  readonly site_id: string;
  readonly domain: string;
  readonly suggestions: readonly BusinessSkillSuggestion[];
  /** The scan is still reading the site — show the group as it arrives. */
  readonly scanning: boolean;
}

export function BusinessSkillSuggestions({
  site_id,
  domain,
  suggestions,
  scanning,
}: BusinessSkillSuggestionsProps): JSX.Element | null {
  const C = useC();
  const http = useOptionalHttp();
  const auth = useOptionalAuth();
  const role =
    auth !== null && auth.state.kind === 'authenticated' ? auth.state.user.role : null;
  const can_write = role === 'owner' || role === 'admin';

  const [rows, setRows] = useState<Partial<Record<BusinessSkillId, RowState>>>({});

  // Reflect what is ALREADY saved (a returning merchant) — read only.
  // Never writes; a saved+enabled row simply renders on.
  useEffect(() => {
    if (http === null) return;
    let cancelled = false;
    void listBusinessSkills(http, site_id).then((res) => {
      if (cancelled || res.kind !== 'ok') return;
      setRows((prev) => {
        const next = { ...prev };
        for (const item of res.items) {
          if (item.url === null) continue;
          const cur = next[item.skill_id];
          if (cur !== undefined && (cur.saving || cur.enabled)) continue;
          next[item.skill_id] = {
            draft: item.url,
            enabled: item.enabled,
            saving: false,
            error: null,
            warning: item.warning,
          };
        }
        return next;
      });
    });
    return () => {
      cancelled = true;
    };
  }, [http, site_id]);

  const stateFor = (s: SlotRow): RowState =>
    rows[s.skill_id] ?? { draft: s.url, enabled: false, saving: false, error: null, warning: null };

  const patch = (id: BusinessSkillId, base: RowState, p: Partial<RowState>): void => {
    setRows((prev) => ({ ...prev, [id]: { ...(prev[id] ?? base), ...p } }));
  };

  // The merchant's explicit click — the only path that writes.
  const onToggle = async (s: SlotRow, next: boolean): Promise<void> => {
    const cur = stateFor(s);
    if (http === null || !can_write) return;
    const url = cur.draft.trim();
    if (next && url.length === 0) {
      patch(s.skill_id, cur, { enabled: false, error: SAVE_REASON_COPY.cannot_enable_without_url ?? null });
      return;
    }
    // Optimistic, reverted on failure (the Toggle atom follows `on`).
    patch(s.skill_id, cur, { enabled: next, saving: true, error: null });
    const res = await putBusinessSkill(
      http,
      site_id,
      s.skill_id,
      next ? { url, enabled: true } : { enabled: false },
    );
    if (res.kind === 'ok') {
      patch(s.skill_id, cur, {
        enabled: res.item.enabled,
        draft: res.item.url ?? url,
        saving: false,
        warning: res.item.warning,
      });
      return;
    }
    const error =
      res.kind === 'bad_request'
        ? SAVE_REASON_COPY[res.reason] ?? 'Could not save.'
        : res.kind === 'forbidden'
          ? 'You do not have permission to change this.'
          : res.kind === 'not_found'
            ? 'Site not found.'
            : 'Could not save. Try again.';
    patch(s.skill_id, cur, { enabled: cur.enabled, saving: false, error });
  };

  const kicker = (
    <div className="flex items-center gap-2" style={{ marginBottom: 6 }}>
      <span style={{ fontFamily: monoFont, fontSize: 10, color: C.muted, letterSpacing: 2 }}>
        BUSINESS SKILLS
      </span>
      {scanning ? (
        <span
          className="flex items-center gap-1.5"
          style={{ fontFamily: sansFont, fontSize: 11, color: C.muted }}
          data-testid="onboarding-business-scanning"
        >
          <RefreshCw size={10} style={{ animation: 'spin 1.4s linear infinite' }} />
          Reading…
        </span>
      ) : null}
    </div>
  );

  if (suggestions.length === 0 && scanning) {
    return (
      <div data-testid="onboarding-business-skills" style={{ marginBottom: 4 }}>
        {kicker}
        <div style={{ fontFamily: sansFont, fontSize: 13, color: C.muted, lineHeight: 1.5 }}>
          Looking for pricing, demo and contact pages on{' '}
          <span style={{ color: C.text }}>{domain}</span>.
        </div>
      </div>
    );
  }

  // Founder 2026-10-21 (siteforbiz.com): with nothing detected, the step
  // showed one line and a closed expander, so there was nothing to pick.
  // Offer every business skill with an empty link field instead. Still
  // nothing is saved until the merchant fills a link and toggles it on.
  const slots: readonly SlotRow[] =
    suggestions.length > 0
      ? suggestions.map((s) => ({ skill_id: s.skill_id, url: s.url, detected: true }))
      : BUSINESS_SKILL_SLOTS.map((id) => ({ skill_id: id, url: '', detected: false }));

  return (
    <div data-testid="onboarding-business-skills">
      {kicker}
      {!can_write ? (
        <div style={{ fontFamily: sansFont, fontSize: 11, color: C.muted, marginBottom: 10 }}>
          Your role can view skills but cannot change them.
        </div>
      ) : null}
      <div className="space-y-2">
        {slots.map((s) => {
          const st = stateFor(s);
          const copy = SLOT_COPY[s.skill_id];
          const Icon = ICONS[s.skill_id];
          return (
            <div
              key={s.skill_id}
              data-testid={`onboarding-business-row-${s.skill_id}`}
              data-enabled={st.enabled ? 'true' : 'false'}
              className="p-4"
              style={{
                background: C.surface,
                border: `1px solid ${st.enabled ? C.border : C.borderSoft}`,
                borderRadius: 3,
              }}
            >
              <div className="flex items-start gap-3">
                <div
                  className="flex items-center justify-center flex-shrink-0"
                  style={{
                    width: 30,
                    height: 30,
                    background: C.surface2,
                    border: `1px solid ${C.border}`,
                    borderRadius: 2,
                  }}
                >
                  <Icon size={14} color={C.text} strokeWidth={1.5} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap" style={{ marginBottom: 8 }}>
                    <span style={{ fontFamily: sansFont, fontSize: 14, fontWeight: 500, color: C.text }}>
                      {copy.name}
                    </span>
                    {s.detected ? (
                      <Pill tone="lime" icon={Check}>
                        DETECTED
                      </Pill>
                    ) : null}
                  </div>
                  <input
                    type="url"
                    inputMode="url"
                    spellCheck={false}
                    value={st.draft}
                    readOnly={st.enabled || !can_write}
                    onChange={(e) =>
                      patch(s.skill_id, st, { draft: e.target.value, error: null })
                    }
                    placeholder={copy.placeholder.replace('example.com', domain)}
                    aria-label={`${copy.name} URL`}
                    data-testid={`onboarding-business-url-${s.skill_id}`}
                    className="w-full px-3 py-2 outline-none"
                    style={{
                      fontFamily: monoFont,
                      fontSize: 12,
                      color: st.enabled ? C.text : C.body,
                      background: C.surface2,
                      border: `1px solid ${C.border}`,
                      borderRadius: 2,
                      minWidth: 0,
                    }}
                  />
                  {st.error !== null ? (
                    <div
                      role="alert"
                      style={{ fontFamily: sansFont, fontSize: 12, color: C.coralFg, marginTop: 6 }}
                    >
                      {st.error}
                    </div>
                  ) : st.warning !== null && st.enabled ? (
                    <div style={{ fontFamily: sansFont, fontSize: 12, color: C.amberFg, marginTop: 6 }}>
                      {st.warning}
                    </div>
                  ) : null}
                </div>
                <Toggle
                  on={st.enabled}
                  disabled={!can_write || http === null || st.saving}
                  onChange={(next) => void onToggle(s, next)}
                  aria-label={`Toggle ${copy.name}`}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
